import { createActionGroup, createFeature, createReducer, createSelector, on, props } from '@ngrx/store';
import { zefDialogOpen } from '@zerops/zef/dialog';
import { ZagentAgentType } from '@zerops/zerops/core/zerops-services';
import { FEATURE_NAME } from './zcp-agent-auth-dialog.constant';
import { ZcpAgentAuthDialogMeta, ZcpAgentAuthDialogSuccessNavigation } from './zcp-agent-auth-dialog.model';

export const zcpAgentAuthDialogActions = createActionGroup({
  source: FEATURE_NAME,
  events: {
    'open': props<ZcpAgentAuthDialogMeta>(),
    'manual open': props<{
      serviceStackId: string;
      agentType: ZagentAgentType;
      agentTypes?: ZagentAgentType[];
      clientId: string;
      /**
       * Correlation id for callers that need a truthful open/failed answer —
       * the code-server bridge passes the inbound `eventId` so it can defer its
       * `accepted:true` ack until the dialog-open effect confirms the container
       * resolved (see `manualOpenResult`). Absent for the service-card button
       * and the deploy auto-open path, which don't ack anyone.
       */
      requestId?: string;
      /**
       * Post-success navigation policy, propagated verbatim into the
       * `zcpAgentAuthDialogOpen` meta this dispatch resolves to (see
       * `ZcpAgentAuthDialogMeta.successNavigation`). Absent/undefined keeps
       * every existing caller's default ('control-plane'); only a caller that
       * owns its own post-auth navigation (the onboarding wizard) passes
       * 'none'.
       */
      successNavigation?: ZcpAgentAuthDialogSuccessNavigation;
    }>(),
    /**
     * Result of a correlated `manualOpen` — emitted by the dialog-open effect
     * once it either dispatched the dialog (`ok: true`) or gave up because the
     * container never became resolvable (`ok: false`). Only the bridge listens
     * (matching on `requestId`); it turns this into the deferred ack. Never
     * emitted for a `manualOpen` without a `requestId`.
     */
    'manual open result': props<{
      requestId: string;
      ok: boolean;
    }>(),
    /**
     * Mark a specific agent on a stack as authorized. The `agentType` is
     * required from Phase 1G onwards so multi-agent stacks track per-agent
     * authorization state correctly. Falls back to the primary agent for
     * legacy callers that omit it.
     */
    'mark authorized': props<{ serviceStackId: string; agentType?: ZagentAgentType }>(),
    /**
     * Revoke a specific agent's authorization. Removes it from the in-session
     * `authedAgentsByStack` set AND deletes the durable
     * `ZCP_AGENT_OAUTH_<SUFFIX>` userData entry. After this, the next deploy
     * (or manual auth re-trigger) treats the agent as unauthed again.
     */
    'revoke authorization': props<{ serviceStackId: string; agentType: ZagentAgentType }>(),
    /**
     * Post-deploy "set API token" — writes (or updates) both
     * `ZCP_AGENT_TOKEN_<SUFFIX>` and `ZCP_AGENT_AUTH_TYPE_<SUFFIX>=token`
     * and restarts the stack so the runtime picks up the new env. After
     * the restart the agent reads as authed (token-mode) by the
     * extractor. Used by the service-card popover so users can flip an
     * unauthed agent to token mode without a redeploy.
     */
    'set api token': props<{ serviceStackId: string; agentType: ZagentAgentType; token: string }>(),
    /**
     * Post-deploy "remove API token" — the inverse of `set api token`:
     * deletes `ZCP_AGENT_TOKEN_<SUFFIX>` and the provider CLI key it wrote,
     * flips `ZCP_AGENT_AUTH_TYPE_<SUFFIX>` back to `oauth` and restarts the
     * stack. The agent falls back to its OAuth state (authorized when its
     * `ZCP_AGENT_OAUTH_<SUFFIX>` flag is set, otherwise unauthed).
     */
    'remove api token': props<{ serviceStackId: string; agentType: ZagentAgentType }>()
  }
});

export const zcpAgentAuthDialogOpen = (meta: ZcpAgentAuthDialogMeta) =>
  zefDialogOpen({ key: FEATURE_NAME, meta });

// # State — tracks authed agents per stack (Phase 1G+) alongside the legacy
// `authedServiceStackIds` set so service-card consumers that haven't migrated
// to per-agent reads stay correct. The legacy set is dropped in Phase 5 cleanup.
export interface ZcpAgentAuthDialogState {
  /** @deprecated since 1G — kept for service-card back-compat. Use `authedAgentsByStack`. */
  authedServiceStackIds: string[];
  /** Per-stack list of authed agent types. Source of truth from Phase 1G onwards. */
  authedAgentsByStack: Record<string, ZagentAgentType[]>;
}

const initialState: ZcpAgentAuthDialogState = {
  authedServiceStackIds: [],
  authedAgentsByStack: {}
};

export const zcpAgentAuthDialogFeature = createFeature({
  name: FEATURE_NAME,
  reducer: createReducer(
    initialState,
    on(zcpAgentAuthDialogActions.markAuthorized, (state, { serviceStackId, agentType }) => {
      // Update legacy set (stack-level boolean) — kept for back-compat
      const nextStackIds = state.authedServiceStackIds.includes(serviceStackId)
        ? state.authedServiceStackIds
        : [ ...state.authedServiceStackIds, serviceStackId ];

      // Update per-agent map. When agentType is omitted (pre-1G callers) we
      // only bump the legacy set — per-agent state stays unchanged because
      // we have no idea which agent the caller meant.
      if (!agentType) {
        return { ...state, authedServiceStackIds: nextStackIds };
      }

      const currentForStack = state.authedAgentsByStack[serviceStackId] ?? [];
      const nextForStack = currentForStack.includes(agentType)
        ? currentForStack
        : [ ...currentForStack, agentType ];

      return {
        authedServiceStackIds: nextStackIds,
        authedAgentsByStack: {
          ...state.authedAgentsByStack,
          [serviceStackId]: nextForStack
        }
      };
    }),
    on(zcpAgentAuthDialogActions.revokeAuthorization, (state, { serviceStackId, agentType }) => {
      const currentForStack = state.authedAgentsByStack[serviceStackId] ?? [];
      const nextForStack = currentForStack.filter((t) => t !== agentType);
      const nextByStack = { ...state.authedAgentsByStack, [serviceStackId]: nextForStack };
      // Drop the legacy stack-level flag only when the LAST authed agent on
      // the stack is revoked — otherwise the legacy reader would falsely
      // treat the stack as fully unauthed while other agents are still authed.
      const nextStackIds = nextForStack.length === 0
        ? state.authedServiceStackIds.filter((id) => id !== serviceStackId)
        : state.authedServiceStackIds;
      return { authedServiceStackIds: nextStackIds, authedAgentsByStack: nextByStack };
    })
  )
});

export const { selectAuthedServiceStackIds, selectAuthedAgentsByStack } = zcpAgentAuthDialogFeature;

/**
 * Per-stack selector — returns the list of agent types authed in THIS session
 * for the given stack. Combine with `extractServiceMetadataMulti`'s
 * `agentConfigs[t].authorized` for the durable view that survives reload.
 */
export const selectAuthedAgentsForStack = (serviceStackId: string) => createSelector(
  selectAuthedAgentsByStack,
  (byStack) => byStack[serviceStackId] ?? []
);
